import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { Offer } from '../types';
import './AdminManualReview.css';

const AdminManualReview: React.FC = () => {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { userProfile } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Mirror exact AdminSettings pattern: only redirect when profile is loaded AND not admin
    if (userProfile && userProfile.role !== 'admin') {
      navigate('/');
      return;
    }
    if (!userProfile) return; // still loading profile — wait for re-run

    loadOffers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile, navigate]);

  const loadOffers = async () => {
    setLoading(true);
    setError('');
    try {
      const snapshot = await getDocs(
        query(collection(db, 'offers'), where('parsingStatus', '==', 'manual_review'))
      );
      const results = snapshot.docs
        .map(d => ({ id: d.id, ...(d.data() as Omit<Offer, 'id'>) }))
        .sort((a: any, b: any) => {
          const aMs = a.submissionDate?.toMillis ? a.submissionDate.toMillis() : 0;
          const bMs = b.submissionDate?.toMillis ? b.submissionDate.toMillis() : 0;
          return bMs - aMs;
        });
      setOffers(results);
    } catch (err: any) {
      setError(err?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  // Mirror AdminSettings: return null for non-admin (prevents flash before redirect)
  if (!userProfile || userProfile.role !== 'admin') {
    return null;
  }

  if (loading) {
    return (
      <div className="amr-page">
        <p className="amr-muted">Loading submissions…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="amr-page">
        <p className="amr-error-text">Error: {error}</p>
        <button className="amr-btn-secondary" onClick={loadOffers}>Retry</button>
      </div>
    );
  }

  return (
    <div className="amr-page">
      <div className="amr-page-header">
        <div>
          <h1 className="amr-page-title">Manual Review Queue</h1>
          <p className="amr-page-desc">
            Offers that could not be automatically processed — enter data manually to complete them.
          </p>
        </div>
        <button className="amr-btn-secondary" onClick={loadOffers}>↻ Refresh</button>
      </div>

      {offers.length === 0 ? (
        <div className="amr-empty">
          <span className="amr-empty-icon">✓</span>
          <h3>Queue is clear</h3>
          <p>No offers are pending manual review.</p>
        </div>
      ) : (
        <div className="amr-card">
          <table className="amr-table">
            <thead>
              <tr>
                <th>Submitted</th>
                <th>Agent</th>
                <th>Property / Address</th>
                <th>Reason</th>
                <th>Files</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {offers.map((offer: any) => (
                <tr key={offer.id}>
                  <td className="amr-nowrap">{fmtDate(offer.submissionDate)}</td>
                  <td>
                    <div className="amr-bold">{offer.buyerAgentName || offer.submittedByName || '—'}</div>
                    <div className="amr-muted">{offer.buyerAgentEmail || offer.submittedByEmail || ''}</div>
                  </td>
                  <td>{getAddress(offer)}</td>
                  <td>
                    <span className="amr-warn-pill" title={offer.parsingError || ''}>
                      {(offer.parsingError || 'Processing error').slice(0, 70)}
                      {(offer.parsingError || '').length > 70 ? '…' : ''}
                    </span>
                  </td>
                  <td>
                    <div className="amr-file-list">
                      {(offer.files || []).map((f: any, i: number) =>
                        f.downloadURL ? (
                          <a key={i} href={f.downloadURL} target="_blank" rel="noopener noreferrer"
                            className="amr-file-link" title={f.fileName}>
                            PDF {i + 1}
                          </a>
                        ) : (
                          <span key={i} className="amr-muted">{f.fileName || `File ${i + 1}`}</span>
                        )
                      )}
                      {!(offer.files?.length) && <span className="amr-muted">—</span>}
                    </div>
                  </td>
                  <td>
                    <button
                      className="amr-review-btn"
                      onClick={() => navigate(`/review-queue/${offer.id}`)}
                    >
                      Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(val: any): string {
  if (!val) return '—';
  try {
    const d = val.toDate ? val.toDate() : new Date(val);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return '—'; }
}

function getAddress(offer: any): string {
  const ext = offer.extractedData;
  return ext?.property?.address || ext?.property?.city || offer.manualPropertyAddress || '—';
}

export default AdminManualReview;
